
import { ViewPaymentPlanCreate_RecipientRecordDocument, ViewPaymentPlanCreate_RecipientRecordQueryVariables } from '@/generated/graphql';
import { mapGetters } from 'vuex';
import { paymentPlanCreate } from '@/api/payment-plan';
import { addDays, differenceInDays } from 'date-fns';
import { defineComponent } from 'vue';
import { GET_ACTION } from '@/gql';
import { Payment_Plan_Conditions } from '@/schema/legal_entity/payment_plan/conditions';

export default defineComponent({
	name: 'CreatePaymentPlan',
	props: {
		recipient: { type: String, required: false, default: '' }
	},
	apollo: {
		recipientRecord: {
			query: ViewPaymentPlanCreate_RecipientRecordDocument,
			variables(): ViewPaymentPlanCreate_RecipientRecordQueryVariables {
				return {
					'participant': this.selectedParticipantStore,
					'recipient': this.recipient
				};
			},
			update: data => data.risolto_recipient[0]
		}
	},
	data() {
		return {
			MIN_PERIOD: 2,
			MAX_PERIOD: 12,
			loading: false,
			form: {
				invoices: [],
				creditNotes: [],
				debitNotes: [],
				payments: [],
				conditions: {
					installmentFrequency: 'MONTHLY',
					interest: 0,
					indemnification: 0,
					indemnificationMax: 0,
					indemnificationMin: 0,
					interestApplied: false,
					indemnificationApplied: false
				},
				constraints: {
					maxInstallments: 2,
					maxInstallmentAmount: null
				},
				dateFirstInstallment: addDays(new Date(), 14),
				selectedConstraintType: 'MAX_INSTALLMENTS'
			},
			constraintInputDraft: '2',
			constraintInputWarning: ''
		};
	},
	computed: {
		...mapGetters(['selectedParticipantStore']),
		validateDateFirstInstallment() {
			const value = this.form.dateFirstInstallment;
			if (value) {
				const today = new Date();
				const diff = differenceInDays(value, today);
				if (diff < 7 || diff > 35) {
					return this.$t('payment_plan.create.date_first_installment_error');
				}
			}
			return null;
		},
		totalAmount() {
			const invoicesAmount = this.form.invoices.reduce((sum, invoice) => {
				return sum + invoice.open_amount;
			}, 0.00);
			const creditNotesAmount = this.form.creditNotes.reduce((sum, creditNote) => {
				return sum + creditNote.open_amount;
			}, 0.00);
			const debitNotesAmount = this.form.debitNotes.reduce((sum, debitNote) => {
				return sum + debitNote.open_amount;
			}, 0.00);
			const paymentAmounts = this.form.payments.reduce((sum, payment) => {
				return sum + payment.open_amount;
			}, 0.00);

			return invoicesAmount - creditNotesAmount + debitNotesAmount - paymentAmounts;
		},
		totalInterest() {
			return this.form.invoices.reduce((sum, invoice) => {
				const openAmount = invoice.open_amount;
				const interestRate = (this.form.conditions.interest || 0.00) / 100;
				const interest = calculateInterest(openAmount, interestRate, invoice.due_date);
				return sum + interest;
			}, 0);
		},
		totalIndemnification() {
			return calculateIndemnification(
				this.totalAmount,
				(this.form.conditions.indemnification || 0.00) / 100,
				this.form.conditions.indemnificationMin,
				this.form.conditions.indemnificationMax
			);
		},
		totalWithConditions() {
			let totalWithConditions = this.totalAmount;
			if (this.form.conditions.interestApplied) {
				totalWithConditions += this.totalInterest;
			}
			if (this.form.conditions.indemnificationApplied) {
				totalWithConditions += this.totalIndemnification;
			}
			return totalWithConditions;
		},
		minAmount() {
			const minAmount = this.form.constraints.maxInstallments ? (this.totalWithConditions / this.form.constraints.maxInstallments) : 0;

			return parseFloat((Math.round(minAmount * 100) / 100).toFixed(2));
		},
		minInstallmentAmount() {
			const minInstallmentAmount = this.MAX_PERIOD ? (this.totalWithConditions / this.MAX_PERIOD) : 0;

			return parseFloat((Math.round(minInstallmentAmount * 100) / 100).toFixed(2));
		},
		maxAmount() {
			const totalAmount = this.totalWithConditions > 0 ? this.totalWithConditions : Math.max(this.totalAmount, 0);
			const maxAmount = this.MIN_PERIOD > 0 ? (totalAmount / this.MIN_PERIOD) : 0;

			return parseFloat((Math.round(maxAmount * 100) / 100).toFixed(2));
		},
		selectedConstraintType: {
			get() {
				return this.form.selectedConstraintType;
			},
			set(value) {
				this.form.selectedConstraintType = value;
				this.constraintInputWarning = '';
				this.syncConstraintInputDraft();
			}
		},
		constraintInputMin() {
			if (this.selectedConstraintType === 'MAX_INSTALLMENTS') {
				return this.MIN_PERIOD;
			}
			return this.minInstallmentAmount;
		},
		constraintInputMax() {
			if (this.selectedConstraintType === 'MAX_INSTALLMENTS') {
				return this.MAX_PERIOD;
			}
			return this.maxAmount;
		},
		constraintInputStep() {
			if (this.selectedConstraintType === 'MAX_INSTALLMENTS') {
				return 1;
			}
			return 0.01;
		},
		displayInstallmentAmount() {
			if (this.selectedConstraintType === 'MAX_INSTALLMENT_AMOUNT' && this.form.constraints.maxInstallmentAmount > 0) {
				return this.form.constraints.maxInstallmentAmount;
			}

			return this.minAmount;
		},
		previewPeriodLabelKey() {
			return this.form.conditions.installmentFrequency === 'WEEKLY'
				? 'payment_plan.create.payment_example_week'
				: 'payment_plan.create.payment_example_month';
		},
		paymentPreview() {
			if (this.selectedConstraintType !== 'MAX_INSTALLMENT_AMOUNT' || this.form.constraints.maxInstallmentAmount <= 0 || this.totalWithConditions <= 0) {
				return {
					visible: false,
					installments: 0,
					regularAmount: 0,
					lastAmount: 0
				};
			}

			const regularAmount = this.form.constraints.maxInstallmentAmount;
			const installments = Math.ceil(this.totalWithConditions / regularAmount);
			const rawLastAmount = this.totalWithConditions - (regularAmount * (installments - 1));
			const lastAmount = parseFloat((Math.round(rawLastAmount * 100) / 100).toFixed(2));

			return {
				visible: true,
				installments,
				regularAmount,
				lastAmount
			};
		}
		// maxAmount() {
		// 	const maxAmount = (this.totalWithConditions / this.MIN_PERIOD) || this.totalAmount.value;

		// 	return parseFloat((Math.round(maxAmount * 100) / 100).toFixed(2));
		// }
	},
	watch: {
		recipientRecord() {
			const payment_plan_conditions: Payment_Plan_Conditions = this.recipientRecord.legal_entity_by_reference.payment_plan_conditions;
			if (!payment_plan_conditions) {
				return;
			}
			this.form.conditions.installmentFrequency = payment_plan_conditions.frequency;

			this.form.conditions.interest = payment_plan_conditions.interest.rate_percentage;
			this.form.conditions.interestApplied = payment_plan_conditions.interest.enabled_by_default;

			this.form.conditions.indemnification = payment_plan_conditions.indemnification.percentage;
			this.form.conditions.indemnificationMin = payment_plan_conditions.indemnification.minimum_amount;
			this.form.conditions.indemnificationMax = payment_plan_conditions.indemnification.maximum_amount;
			this.form.conditions.indemnificationApplied = payment_plan_conditions.indemnification.enabled_by_default;

			this.MAX_PERIOD = payment_plan_conditions.maximum_duration ? payment_plan_conditions.maximum_duration.duration : 12;
			this.form.constraints.maxInstallmentAmount = this.maxAmount;
			this.constraintInputWarning = '';
			this.syncConstraintInputDraft();
		}
	},
	methods: {
		getSuggestedInstallmentAmount() {
			if (this.minInstallmentAmount > 0) {
				return this.minInstallmentAmount;
			}
			if (this.maxAmount > 0) {
				return this.maxAmount;
			}
			return null;
		},
		syncConstraintInputDraft() {
			if (this.selectedConstraintType === 'MAX_INSTALLMENTS') {
				const installments = this.form.constraints.maxInstallments ?? this.MIN_PERIOD;
				this.constraintInputDraft = String(installments);
				return;
			}

			const amount = this.form.constraints.maxInstallmentAmount > 0
				? this.form.constraints.maxInstallmentAmount
				: this.getSuggestedInstallmentAmount();
			if (!amount || amount <= 0) {
				this.constraintInputDraft = '';
				return;
			}

			this.form.constraints.maxInstallmentAmount = amount;
			this.constraintInputDraft = String(parseFloat(amount.toFixed(2)));
		},
		commitConstraintInput() {
			const rawValue = (this.constraintInputDraft ?? '').toString().trim().replace(',', '.');
			if (!rawValue) {
				this.constraintInputWarning = `${this.$t('validator.invalid_number')} (${this.constraintInputMin} - ${this.constraintInputMax})`;
				return;
			}

			const parsedValue = Number(rawValue);
			if (Number.isNaN(parsedValue)) {
				this.constraintInputWarning = `${this.$t('validator.invalid_number')} (${this.constraintInputMin} - ${this.constraintInputMax})`;
				return;
			}

			const min = this.constraintInputMin;
			const max = this.constraintInputMax;
			if (this.selectedConstraintType === 'MAX_INSTALLMENTS' && !Number.isInteger(parsedValue)) {
				this.constraintInputWarning = `${this.$t('validator.invalid_number')} (${min} - ${max})`;
				return;
			}
			if (parsedValue < min || parsedValue > max) {
				this.constraintInputWarning = `${this.$t('validator.invalid_number')} (${min} - ${max})`;
				return;
			}

			this.constraintInputWarning = '';
			if (this.selectedConstraintType === 'MAX_INSTALLMENTS') {
				this.form.constraints.maxInstallments = parsedValue;
				return;
			}

			this.form.constraints.maxInstallmentAmount = parseFloat(parsedValue.toFixed(2));
		},
		handleConstraintInputChange() {
			this.constraintInputWarning = '';
		},
		async showAction(action: string) {
			const result = await this.$apollo.query({
				query: GET_ACTION,
				variables: {
					participant: this.$store.state.risolto.selectedParticipantStore,
					action: action
				}
			});
			const actionRecord = result.data.action[0];

			this.$store.commit('actions/UPSERT_ACTION', actionRecord);
			this.$store.commit('actions/SET_SELECTED_ACTION_REF', actionRecord.reference);
			this.$store.commit('risolto/SET_SELECTED_RECIPIENT', actionRecord.recipient_by_reference);
			this.$store.commit('risolto/SET_COMPONENT_TYPE_STORE', actionRecord.channel);
			this.$store.commit('risolto/SET_ACTION_DETAIL_DIALOG_VISIBLE', true);
		},
		goToRecipient() {
			this.$router.push({
				name: 'recipient-detail',
				params: {
					id: this.recipient,
					participant: this.selectedParticipantStore
				}
			});
		},
		goToEdit(payment_plan_id) {
			this.$router.push({
				name: 'edit-payment-plan',
				params: {
					id: payment_plan_id,
					participant: this.selectedParticipantStore,
					recipient: this.recipient
				}
			});
		},
		handleGoBack() {
			this.goToRecipient();
		},
		handleInvoiceSelectionChange(selection) {
			this.form.invoices = selection;
		},
		handleCreditNoteSelectionChange(selection) {
			this.form.creditNotes = selection;
		},
		handleDebitNoteSelectionChange(selection) {
			this.form.debitNotes = selection;
		},
		handlePaymentSelectionChange(selection) {
			this.form.payments = selection;
		},
		async handleFormSubmit() {
			if (this.loading) {
				return;
			}
			this.commitConstraintInput();
			if (this.constraintInputWarning) {
				return;
			}
			this.$refs['paymentPlanForm'].validate((valid) => {
				if (valid) {
					this.formSubmit();
				} else {
					return false;
				}
			});
		},
		async formSubmit() {
			this.loading = true;
			try {
				const response = await paymentPlanCreate(
					this.selectedParticipantStore,
					this.recipientRecord.legal_entity_by_reference.reference,
					this.recipientRecord.reference,
					this.form.invoices.map((invoice) => { return invoice.reference; }),
					this.form.creditNotes.map((creditNote) => { return creditNote.reference; }),
					this.form.debitNotes.map((debitNote) => { return debitNote.reference; }),
					this.form.payments.map((payment) => { return payment.reference; }),
					this.form.conditions.interest,
					this.form.conditions.interestApplied,
					this.form.conditions.indemnification,
					this.form.conditions.indemnificationApplied,
					this.form.conditions.indemnificationMin,
					this.form.conditions.indemnificationMax,
					this.form.conditions.installmentFrequency,
					this.selectedConstraintType === 'MAX_INSTALLMENTS' ? this.form.constraints.maxInstallments : null,
					this.selectedConstraintType === 'MAX_INSTALLMENT_AMOUNT'
						? {
							amount: {
								value: this.form.constraints.maxInstallmentAmount,
								currency: this.recipientRecord.invoice_documents[0]?.amount_currency || this.recipientRecord.currency
							},
							frequency: this.form.conditions.installmentFrequency
						}
						: null,
					this.form.dateFirstInstallment
				);
				await this.showAction(response.data.action);

				this.loading = false;

				// this.goToRecipient();
				this.goToEdit(response.data.id);
			} catch (error) {
				const response = error.response;
				if (response && response.data && response.data.message_code) {
					this.$message.error(this.$t(`error.${response.data.message_code}`));
				} else {
					this.$message.error(this.$t('somethingWentWrong'));
				}
			} finally {
				this.loading = false;
			}
		}
	}
});

const DAYS_IN_YEAR = 365;

function getDaysOverdue(dueDateString) {
	const today = new Date(Date.now());
	const dueDate = new Date(dueDateString);
	const diffInDays = differenceInDays(today, dueDate);
	return diffInDays > 0 ? diffInDays : 0;
}

function calculateInterest(amount, yearlyInterestRate, dueDateString) {
	const daysOverdue = getDaysOverdue(dueDateString);
	const dailyInterestRate = yearlyInterestRate / DAYS_IN_YEAR;
	return amount * dailyInterestRate * daysOverdue;
}

function calculateIndemnification(amount, rate, min, max) {
	const indemnification = amount * rate;
	return Math.max(Math.min(indemnification, max), min);
}

